import { CircularProgress, Typography } from "@mui/material";
import lodash from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../BrandOnBoarding/api";
import Modal from "../../Component/Modal/Modal";
import MUIAutocomplete from "../../Component/UI/MUIAutocomplete/MUIAutocomplete";
import MUIButton from "../../Component/UI/MUIButton/MUIButton";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import RadioGroup from "../../Component/UI/RadioGroup/RadioGroup";
import Warning from "../../Component/Warning/Warning";
import { LLM_OPTIONS } from "../../utils/constant";
import { validateBrandInput, validateProductInput } from "../../utils/helpers";
import { getCategoryPrompt } from "../../utils/prompt";
import Suggestions from "../CategoryDiscovery/Suggestions/Suggestions";
import TextInput from "../../Component/UI/TextInput/TextInput";
import AttributeInput from "../CategoryDiscovery/AttributeInput/AttributeInputV2";
import classes from "./Form.module.css";
import ProductVariant from "./ProductVariant";
import UserKeyPlayer from "./UserKeyPlayer";
import { DiscoveryContext } from "./discovery-context";
import useCountry from "./useCountry";
import useGeneratePreCategoryDimensions from "./useGeneratePreCategoryDimensions";
import useGenerateProductTaxonomies from "./useGenerateProductTaxonomies";
import useProductPreference from "./useProductPreference";
import useTopUserProducts from "./useTopUserProducts";
import { Checkbox, FormControlLabel } from "@mui/material";
import useHistory from "./History/useHistory";
import { getCategoryTypePrompt } from "../../utils/prompts/prompts.en";

const RADIO_OPTIONS = [
  {
    value: "brand",
    label: "Brand",
  },
  {
    value: "product",
    label: "Product",
  },
];

export default function Form({
  onSubmit,
  onTypeChanged = () => { },
  onReset = () => { },
  warningText,
  setWarningText = () => { },
  cancelTransaction,
  stopPolling = () => { },
  setCancelTransaction = () => { },
}) {
  const discoveryCtx = useContext(DiscoveryContext);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showManualKeyPlayerInput, setShowManualKeyPlayerInput] =
    useState(false);
  const [productVariants, setProductVariants] = useState([]);
  const competitorsRef = useRef(discoveryCtx.competitors);

  const [brand, setBrand] = useState("");
  const [discoveryType, setDiscoveryType] = useState("brand");
  const [llm, setLLM] = useState("gpt-4o");
  const [category, setCategory] = useState("");
  const [categoryType, setCategoryType] = useState("");
  const [market, setMarket] = useState("");
  const [selectedBrandsProducts, setSelectedBrandProducts] = useState([]);
  const [prefLoading, setPrefLoading] = useState(false);
  const [showManualCategoryInput, setShowManualCategoryInput] = useState(false);
  const [showManualCategoryTypeInput, setShowManualCategoryTypeInput] = useState(false);
  const [manualCategoryInput, setManualCategoryInput] = useState("");
  const [manualCategoryTypeInput, setManualCategoryTypeInput] = useState("");
  const [aICategories, setAICategories] = useState([]);
  const [aICategoryTypes, setAICategoryTypes] = useState([]);
  const [isMock, setIsMock] = useState(false);
  const [isCategorySuggestionLoading, setCategorySuggestionLoading] =
    useState(false);
  const [isCategoryTypeSuggestionLoading, setCategoryTypeSuggestionLoading] =
    useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const categoryChangeTimerRef = useRef();
  const keyPlayerChangeTimerRef = useRef();

  const {
    loading: keyPlayersLoading,
    keyPlayers,
    setKeyPlayers,
    getBrandOrProductSuggestions,
  } = useTopUserProducts();
  const {
    deleteHistory,
  } = useHistory(discoveryType);
  const { getProductPrefs } = useProductPreference();
  const { generateProductTaxonomies } = useGenerateProductTaxonomies();
  const { generatePreCategoryDimensions } = useGeneratePreCategoryDimensions();

  const timerRef = useRef();
  const { countries } = useCountry();

  const { location, occupation } = discoveryCtx;

    let hasRequiredCompetitors =
    discoveryType === "category"
      ? discoveryCtx.competitors.length > 0
      : discoveryCtx.competitors.length === 4;

  useEffect(() => {
    if (!category || discoveryCtx.state.id) return;
    if (categoryChangeTimerRef.current) {
      clearTimeout(categoryChangeTimerRef.current);
    }
    discoveryCtx.updateProductPrefs([]);
    categoryChangeTimerRef.current = setTimeout(() => {
      if (discoveryCtx.type === "category" || discoveryCtx.type === "product") {
        generateProductTaxonomies(category, llm);
        setPrefLoading(true);
        if (discoveryCtx.type === "category") {
          getProductPrefs()
            .then(() => setPrefLoading(false))
            .catch(() => setPrefLoading(false));
        }
      }
      // generatePreCategoryDimensions(category);
    }, 1000);
  }, [category, llm, brand]);

  useEffect(() => {
    if (!brand || discoveryCtx.is_launched) {
      setAICategories([]);
      return;
    }
    async function getCategoryAISuggestions() {
      setCategorySuggestionLoading(true);
      let language = discoveryCtx.language;
      console.log("language", language);
      try {
        const prompt = getCategoryPrompt(brand, language);
        const payload = {
          llm: llm,
          prompt: prompt,
        };

        let response = await api.post(`/llm-execute`, payload);
        const result = response?.data?.data || [];

        if (result) {
          let resultArray = result;
          setAICategories(
            resultArray.categories.map((o) => ({ name: o, value: o }))
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setCategorySuggestionLoading(false);
      }
    }

    const debouncedFunction = lodash.debounce(getCategoryAISuggestions, 2000);
    debouncedFunction();
    return () => debouncedFunction.cancel();
  }, [brand, discoveryCtx.is_launched, discoveryCtx.language, llm]);

  useEffect(() => {
    if (!brand || discoveryCtx.is_launched || !category) {
      setAICategoryTypes([]);
      return;
    }
    async function getCategoryTypeAISuggestions() {
      setCategoryTypeSuggestionLoading(true);
      let language = discoveryCtx.language;
      console.log("language", language);
      try {
        const prompt = getCategoryTypePrompt(brand, category, language);
        const payload = {
          llm: llm,
          prompt: prompt,
        };

        let response = await api.post(`/llm-execute`, payload);
        const result = response?.data?.data || [];

        if (result) {
          let resultArray = result;
          setAICategoryTypes(
            resultArray.categoryTypes.map((o) => ({ name: o, value: o }))
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        setCategoryTypeSuggestionLoading(false);
      }
    }

    const debouncedFunction = lodash.debounce(getCategoryTypeAISuggestions, 2000);
    debouncedFunction();
    return () => debouncedFunction.cancel();
  }, [brand, discoveryCtx.is_launched, category]);

  useEffect(() => {
    console.log(
      "category",
      category,
      "brand",
      brand,
      "discoveryCtx.state.id",
      discoveryCtx.state.id,
      discoveryCtx.type
    );

    if (!category || discoveryCtx.state.id || !brand || cancelTransaction) {
      return;
    }
    if (keyPlayerChangeTimerRef.current) {
      clearTimeout(keyPlayerChangeTimerRef.current);
    }
    discoveryCtx.updatedCompetitors(
      discoveryCtx.competitors.filter(
        (comp) =>
          comp &&
          comp?.name.toString().toLowerCase() ===
          discoveryCtx.entity.toString().toLowerCase()
      )
    );
    keyPlayerChangeTimerRef.current = setTimeout(() => {
      if (discoveryCtx.type === "category" || discoveryCtx.type === "product")
        getBrandOrProductSuggestions(
          "product",
          category,
          null,
          [],
          null,
          market,
          brand
        );
      if (discoveryCtx.type === "brand")
        getBrandOrProductSuggestions(
          "brand",
          category,
          null,
          [],
          null,
          market,
          brand
        );
    }, 1000);
  }, [discoveryCtx.state.id, brand, discoveryType, category, market, llm]);

  function handleProductBrandSelection(player) {
    setSelectedBrandProducts((prevPlayers) => {
      let message = "Only 4 key players are allowed";
      let maxProductBrands = 4;
      if (discoveryCtx.type !== "category") {
        maxProductBrands = 3;
        message = "Only 3 competitors are allowed";
      }
      if (prevPlayers.length == maxProductBrands) {
        toast.warn(message);
        return prevPlayers;
      }

      const isPlayerExist = prevPlayers.find(
        (item) => item.name === player.name
      );
      if (isPlayerExist || !player) return prevPlayers;
      discoveryCtx.updatedCompetitors([
        ...discoveryCtx.competitors,
        ...prevPlayers,
        { ...player, name: player.value },
      ]);
      return [...prevPlayers, { ...player, name: player.value }];
    });
  }

  function handleDeleteBrandProduct(itemName) {
    setSelectedBrandProducts((prevBrandProducts) => {
      return prevBrandProducts.filter((item) => item.name != itemName);
    });
    const updatedCompetitors = discoveryCtx.competitors.filter(
      (comp) =>
        comp.name.toString().toLowerCase() !== itemName.toString().toLowerCase()
    );
    discoveryCtx.updatedCompetitors(updatedCompetitors);
  }

  useEffect(() => {
    if (discoveryCtx.abort_launch || !discoveryCtx.is_launched) {
      setSelectedBrandProducts([]);
    }
  }, [discoveryCtx.abort_launch, discoveryCtx.is_launched]);

  useEffect(() => {
    if (selectedBrandsProducts.length > 0) return;
    if (discoveryCtx.state.id) {
      setKeyPlayers([]);
      return;
    }
    if (keyPlayers.length === 8 && discoveryType !== "category") {
      const updatedCompetitors = [...discoveryCtx.competitors].concat(
        keyPlayers.slice(0, 3)
      );
      discoveryCtx.updatedCompetitors(updatedCompetitors);
      setSelectedBrandProducts([...keyPlayers.slice(0, 3)]);
    }
  }, [
    keyPlayers,
    discoveryType,
    discoveryCtx.category,
    discoveryCtx.state.id,
    // selectedBrandsProducts.length,
  ]);

  useEffect(() => {
    competitorsRef.current = discoveryCtx.competitors;
  }, [discoveryCtx.competitors]);

  async function checkIfInputValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");
    try {
      let response = await validateBrandInput(input, category);
      setLoading(false);
      const latestCompetitors = competitorsRef.current;
      const updatedCompetitors = [...latestCompetitors];
      updatedCompetitors.unshift({
        name: brand,
        domain: response.data.domain,
      });
      discoveryCtx.updatedCompetitors(updatedCompetitors);
      if (response.data.ans !== "Yes") {
        setWarningText(
          `The ${discoveryType} or category name appears to be incorrect. This may impact the Brand Index report.`
        );
        // setSuggestions([]);
        setSuggestions(response?.data?.correct_inputs);
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  async function checkIfProductValid(input) {
    setLoading(true);
    setSuggestions([]);
    setWarningText("");
    setProductVariants([]);
    try {
      let response = await validateProductInput(input, category);
      setLoading(false);
      const variants = response?.data?.variants || [];
      const updatedVariants = variants.map((variant) => {
        return {
          id: variant,
          name: variant,
          active: true,
        };
      });
      setProductVariants(updatedVariants);
      /* */
      const latestCompetitors = competitorsRef.current;
      const updatedCompetitors = [...latestCompetitors];
      updatedCompetitors.unshift({
        name: brand,
        domain: response.data.domain,
        variants: updatedVariants,
      });

      discoveryCtx.updatedCompetitors(updatedCompetitors);

      if (response.data.ans !== "Yes") {
        if (
          new String(response?.data?.correct_inputs[0])
            .toLocaleLowerCase()
            .trim() !== new String(discoveryCtx.entity).toLowerCase().trim()
        ) {
          setSuggestions(response?.data?.correct_inputs);
          setWarningText(
            `The ${discoveryType} or category name appears to be incorrect. This may impact the Brand Index report.`
          );
        }
      }
    } catch (error) {
      console.log("Error while validating input", error);
      setLoading(false);
    }
  }

  function handleSuggestionClick(text) {
    discoveryCtx.updatedCompetitors([]);
    setBrand(text);
    setSuggestions([]);
    setSelectedBrandProducts([]);
  }

  function handleBrandInputChange(e) {
    setBrand(e.target.value);
    resetState();
    setCategory("");
    setCategoryType("");
  }

  console.log("test==>", discoveryCtx.competitors, selectedBrandsProducts);

  function resetState() {
    discoveryCtx.updateAbortLaunch(true);
    setProductVariants([]);
    discoveryCtx.updateId(null);
    discoveryCtx.updatedLaunched(false);
  }

  function handleDiscoveryTypeChange(value) {
    discoveryCtx.resetState();
    onTypeChanged(value);
    discoveryCtx.updateType(value);
    setDiscoveryType(value);
    setBrand("");
    setCategory("");

    setCategoryType("");
    setLLM("gpt-4o");
    setSuggestions([]);
    setProductVariants([]);
    setKeyPlayers([]);
    setSelectedBrandProducts([]);
    onReset();
    setMarket("");
  }

  function deleteTransaction() {
    discoveryCtx.resetState();
    stopPolling();
    deleteHistory(discoveryCtx?.state?.id);
    setShowDeleteDialog(false);
    setBrand("");
    setCategory("");
    setCategoryType("");
    setLLM("gpt-4o");
    setKeyPlayers([]);
    setSelectedBrandProducts({});
    onReset();
    setMarket("");
  }



  useEffect(() => {
    if (
      !brand ||
      discoveryCtx.is_launched ||
      !category ||
      discoveryCtx.state.id
      || cancelTransaction
    ) {
      return;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      if (discoveryType === "product") {
        checkIfProductValid(brand);
      } else {
        checkIfInputValid(brand);
      }
    }, 1000);
  }, [
    brand,
    category,
    discoveryType,
    market,
    llm,
    discoveryCtx.is_launched,
    discoveryCtx.state.id,
  ]);

  useEffect(() => {
    if (discoveryCtx.state.id) {
      setBrand(discoveryCtx.entity);
      setAICategories([
        {
          name: discoveryCtx.category,
          value: discoveryCtx.category,
        },
      ]);
      setAICategoryTypes([
        {
          name: discoveryCtx.categoryType,
          value: discoveryCtx.categoryType,
        },
      ]);
      setCategory(discoveryCtx.category);
      setCategoryType(discoveryCtx.categoryType);
      setMarket(discoveryCtx.market);
      setLLM(discoveryCtx.llm);
      setSelectedBrandProducts(discoveryCtx.competitors.slice(1));
    }
  }, [
    discoveryCtx.state.id,
    discoveryCtx.category,
    // discoveryCtx.llm,
    discoveryCtx.entity,
    discoveryCtx.market,
    discoveryCtx.competitors,
  ]);

  useEffect(() => {
    if (cancelTransaction && !discoveryCtx.abort_launch) {
      setBrand(discoveryCtx.entity);
      setCategory(discoveryCtx.category);
      setCategoryType(discoveryCtx.categoryType);
      setMarket(discoveryCtx.market);
      setLLM(discoveryCtx.llm);
      setCancelTransaction(false);
    }
  }, [cancelTransaction, discoveryCtx.abort_launch])

  const cancelTransactionRecord = () => {
    stopPolling();
    setCancelTransaction(true);
    discoveryCtx.updateAbortLaunch(true);
  }
  const debounceLaunch = lodash.debounce(function () {
    if ((!brand && discoveryType !== "category") || !category || !llm
      // || !categoryType
    ) {
      toast.warn("Please fill all required fields");
      return;
    }

    if (
      discoveryType === "category" &&
      (!discoveryCtx.location ||
        !discoveryCtx.occupation ||
        discoveryCtx.attributes.length === 0)
    ) {
      toast.warn("Please fill all required fields");
      return;
    }



    onSubmit({
      brand,
      category,
      categoryType,
      market,
      llm,
      discoveryType,
      isMock
    });
  }, 1000);


  const updatedKeyPlayers = keyPlayers.map((player) => ({
    ...player,
    value: player.name,
    name: player.display_name,
  }));
  
  return (
    <div className={classes.form}>
      {discoveryCtx.type !== "category" && (
        <div className={classes.header}>
          <div className={classes.radio}>
            <RadioGroup
              disabled={discoveryCtx.loading}
              className={classes.type_radio}
              options={RADIO_OPTIONS}
              onChange={handleDiscoveryTypeChange}
            />
          </div>
          {warningText && <Warning text={warningText} />}
        </div>
      )}
      <div className={classes.input_group}>
        <div className={classes.input_section}>
          {discoveryType !== "category" && (
            <div className={classes.brand_input}>
              <TextInput
                disabled={discoveryCtx.loading}
                fullWidth
                value={brand}
                label={discoveryType === "brand" ? "Brand" : "Product"}
                placeholder={
                  discoveryType === "brand" ? "Nike" : "Nike Air Force 1"
                }
                onChange={handleBrandInputChange}
              />
              {productVariants.length > 0 && (
                <ProductVariant
                  triggerId="basic-menu"
                  variants={productVariants}
                  index={0}
                  editable={!discoveryCtx.loading && !discoveryCtx.is_launched}
                >
                  <span className={classes.variant_text}>
                    {productVariants.length} variants found
                  </span>
                </ProductVariant>
              )}
              {loading && (
                <CircularProgress className={classes.loading} size={15} />
              )}
              {suggestions.length > 0 && (
                <span className={classes.hint_text}>
                  Did you mean:
                  <i
                    className={classes.hint_chip}
                    onClick={() => handleSuggestionClick(suggestions[0])}
                  >
                    {suggestions[0]}
                  </i>
                </span>
              )}
            </div>
          )}
          {discoveryType !== "category" && (
            <MUISelect
              disabled={discoveryCtx.loading}
              label={"LLM"}
              defaultValue="gpt-4o"
              options={LLM_OPTIONS}
              optionValue={llm}
              onChange={(e) => {
                // discoveryCtx.updateLLM(e.target.value);
                resetState();
                setLLM(e.target.value);
              }}
            />
          )}
          {/* <div style={{ flex: 0.5 }}>
            <div className="d-flex flex-column">
              <MUISelect
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                fullWidth
                optionValue={discoveryCtx.language}
                label={"Language"}
                options={discoveryCtx.languageList || []}
                onChange={(e) => {
                  resetState();
                  discoveryCtx.updateLanguage(e.target.value);
                }}
              />
            </div>
          </div> */}

          <MUISelect
            loading={isCategorySuggestionLoading}
            disabled={discoveryCtx.loading}
            fullWidth
            placeholder="eg Athletic Apparel"
            optionValue={category}
            label="Category"
            sx={{
              color: category ? "black" : "#979797", // Gray when placeholder is visible
            }}
            options={[
              ...aICategories,
              {
                name: "Add Manually",
                value: "Add Manually",
                id: 0,
              },
            ]}
            onChange={(e) => {
              const value = e.target.value;
              console.log(value);
              if (value === "Add Manually") {
                if (brand.trim().length === 0) {
                  toast.warn("Please enter brand first");
                  return;
                }
                setManualCategoryInput("");
                setShowManualCategoryInput(true);
                return;
              }
              setCategory(value);
              resetState();
            }}
          />


          {/* <MUISelect
            loading={isCategoryTypeSuggestionLoading}
            disabled={discoveryCtx.loading}
            fullWidth
            placeholder="eg Companies"
            optionValue={categoryType}
            label="Category Type"
            sx={{
              color: categoryType ? "black" : "#979797", // Gray when placeholder is visible
            }}
            options={[
              ...aICategoryTypes,
              {
                name: "Add Manually",
                value: "Add Manually",
                id: 0,
              },
            ]}
            onChange={(e) => {
              const value = e.target.value;
              console.log(value);
              if (value === "Add Manually") {
                if (brand.trim().length === 0) {
                  toast.warn("Please enter brand first");
                  return;
                }
                setManualCategoryTypeInput("");
                setShowManualCategoryTypeInput(true);
                return;
              }
              setCategoryType(value);
              resetState();
            }}
          /> */}

          {discoveryType !== "category" && (
            <div style={{ flex: 1 }}>
              <TextInput
                fullWidth
                label="Market"
                disabled={discoveryCtx.loading}
                placeholder="e.g North America"
                value={market}
                onChange={(e) => {
                  // discoveryCtx.updateMarket(e.target.value);
                  setMarket(e.target.value);
                  resetState();
                }}
              />
            </div>
          )}
          {discoveryType === "category" && (
            <div style={{ flex: 1 }}>
              <div className="d-flex flex-column">
                <TextInput
                  value={occupation}
                  disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                  label="Who will be using this product?"
                  placeholder="e.g Home maker"
                  onChange={(e) =>
                    discoveryCtx.updateOccupation(e.target.value)
                  }
                />
                {!occupation && (
                  <Typography
                    color="textDisabled"
                    variant="caption"
                    sx={{ fontSize: "8px" }}
                  >
                    Please specify who will be using this product.
                  </Typography>
                )}
              </div>
            </div>
          )}
          {discoveryType === "category" && (
            <div style={{ flex: 1 }}>
              <MUIAutocomplete
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                onInputChange={() => {
                  if (location === "United States") return;
                  discoveryCtx.updateLocation("");
                }}
                optionValue={location ?? "United States"}
                label="What country are you in?"
                options={countries.length > 0 ? countries : []}
                onChange={(e) => {
                  if (!e?.value) return;
                  discoveryCtx.updateLocation(e?.value ?? "");
                }}
              />
            </div>
          )}
        </div>
        <div className={classes.input_section}>
          {discoveryCtx.type === "category" && (
            <div style={{ flex: 1 }}>
              <AttributeInput
                prefLoading={prefLoading}
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                label="Attributes you care about"
              />
            </div>
          )}
          <div style={{ flex: 1.5 }}>
            <div className="d-flex flex-column">
              <MUISelect
                loading={keyPlayersLoading}
                disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                fullWidth
                optionValue={""}
                label={
                  discoveryType === "category" ? "Key players " : "Competitors "
                }
                options={[
                  ...updatedKeyPlayers,
                  {
                    name: "Add Manually",
                    domain: "",
                    value: "Add Manually",
                    id: 0,
                  },
                ]}
                onChange={(e) => {
                  if (e.target.value === "Add Manually") {
                    if (category.trim().length === 0) {
                      toast.warn("Please enter category first");
                      return;
                    }
                    setShowManualKeyPlayerInput(true);
                    return;
                  }
                  const option = keyPlayers.find((opt) => {
                    return (
                      opt.name.toString().toLowerCase().trim() ===
                      e.target.value.toString().toLowerCase().trim()
                    );
                  });
                  handleProductBrandSelection({
                    ...option,
                    value: option.name,
                  });
                }}
              />
              {discoveryCtx.competitors.length <= 3 && (
                <Typography
                  color="textDisabled"
                  variant="caption"
                  sx={{ fontSize: "8px" }}
                >
                  Select{" "}
                  {discoveryType === "category"
                    ? "key players "
                    : "competitors "}
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ fontSize: "8px" }}
                  >
                    ({discoveryType === "category" ? `upto 4` : `Exactly 3`})
                  </Typography>
                </Typography>
              )}
              {selectedBrandsProducts.length > 0 && (
                <Suggestions
                  options={selectedBrandsProducts}
                  onDelete={handleDeleteBrandProduct}
                  discoveryCtx={discoveryCtx}
                />
              )}
            </div>
          </div>

          {/* <div className="d-flex flex-column" style={{ flex: 0.5, display: "flex", marginTop: "1.5rem" }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isMock}
                  onChange={(e) => setIsMock(e.target.checked)}
                  disabled={discoveryCtx.loading || discoveryCtx.is_launched}
                />
              }
              label="isMock"
            />
          </div> */}


          <div className="d-flex flex-column" style={{ flex: 0.7 }}>
            {!discoveryCtx?.state?.id && (
              <MUIButton
                variant="contained"
                color={discoveryCtx.loading ? "error" : "primary"}
                disabled={
                  loading ||
                  !category ||
                  // !categoryType ||
                  discoveryCtx.is_launched ||
                  (discoveryType !== "category" && !brand)  ||
                  !hasRequiredCompetitors
                }
                onClick={debounceLaunch}
              >
                Launch
              </MUIButton>
            )}
            {discoveryCtx?.state?.id && (
              <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "20px" }}>
                {discoveryCtx.state.transactionStatus !== "COMPLETED" && (
                  <MUIButton color="error" onClick={() => setShowDeleteDialog(true)}>
                    Cancel
                  </MUIButton>
                )}
                <MUIButton color="primary" onClick={cancelTransactionRecord}>
                  Start New
                </MUIButton>
              </div>
            )}
          </div>
        </div>
      </div>
      {showManualKeyPlayerInput && (
        <UserKeyPlayer
          open={showManualKeyPlayerInput}
          category={category}
          discoveryType={discoveryType}
          handleClose={() => setShowManualKeyPlayerInput(false)}
          handleConfirm={(val, domain = "example.com", variants) => {
            handleProductBrandSelection({
              id: val,
              name: val,
              domain,
              value: val,
              variants,
            });
            setShowManualKeyPlayerInput(false);
          }}
        />
      )}

      {showManualCategoryInput && (
        <Modal
          title="Add Category"
          open={true}
          showActions
          handleClose={() => setShowManualCategoryInput(false)}
          handleConfirm={() => {
            console.log("Confirm: " + manualCategoryInput);
            setAICategories((prev) => [
              ...prev,
              {
                name: manualCategoryInput,
                value: manualCategoryInput,
              },
            ]);
            resetState();
            setCategory(manualCategoryInput);
            setShowManualCategoryInput(false);
          }}
          confirmBtnDisabled={!manualCategoryInput}
        >
          <TextInput
            className={classes.input}
            fullWidth
            type="text"
            value={manualCategoryInput}
            onChange={(e) => setManualCategoryInput(e.target.value)}
            placeholder={"Enter category"}
          />
        </Modal>
      )}

      {showManualCategoryTypeInput && (
        <Modal
          title="Add Category Type"
          open={true}
          showActions
          handleClose={() => setShowManualCategoryInput(false)}
          handleConfirm={() => {
            console.log("Confirm: " + manualCategoryTypeInput);
            setAICategoryTypes((prev) => [
              ...prev,
              {
                name: manualCategoryTypeInput,
                value: manualCategoryTypeInput,
              },
            ]);
            resetState();
            setCategoryType(manualCategoryTypeInput);
            setShowManualCategoryTypeInput(false);
          }}
          confirmBtnDisabled={!manualCategoryTypeInput}
        >
          <TextInput
            className={classes.input}
            fullWidth
            type="text"
            value={manualCategoryTypeInput}
            onChange={(e) => setManualCategoryTypeInput(e.target.value)}
            placeholder={"Enter category Type"}
          />
        </Modal>
      )}

      {discoveryCtx?.state?.id && (
        <Modal
          handleClose={() => setShowDeleteDialog(false)}
          handleConfirm={deleteTransaction}
          open={showDeleteDialog}
          title="Delete History?"
          showActions
          confirmBtnText="Delete"
          confirmBtnColor="error"
        >
          Do you want to delete this item - {discoveryCtx?.entity}?
        </Modal>
      )}
      <ToastContainer />
    </div>
  );
}
